import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Flower2, ShoppingCart, Route, Loader2, Lock, CreditCard, Banknote, Landmark, CalendarX2, MapPinned } from "lucide-react";
import api from "@/lib/api";

const TYPES = [
  { key: "colis", icon: Package, label: "Colis & retraits" },
  { key: "fleurs", icon: Flower2, label: "Fleurs & cadeaux" },
  { key: "drive", icon: ShoppingCart, label: "Drive & courses" },
  { key: "course", icon: Route, label: "Course sur mesure" },
];

const PAYMENT_METHODS = [
  { key: "cb", icon: CreditCard, label: "Carte bancaire", sub: "en ligne, sécurisé" },
  { key: "especes", icon: Banknote, label: "Espèces", sub: "à la livraison" },
  { key: "virement", icon: Landmark, label: "Virement", sub: "RIB par téléphone" },
];

const inputCls =
  "min-h-[48px] w-full rounded-xl border border-ink/15 bg-white px-4 text-base text-ink outline-none transition-colors duration-200 placeholder:text-ink/35 focus:border-forest focus:ring-2 focus:ring-forest/20";

const eur = (n) => n.toFixed(2).replace(".", ",");

export default function BookingForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    delivery_type: "drive",
    pickup_address: "",
    delivery_address: "",
    date: "",
    time_slot: "",
    description: "",
    client_name: "",
    phone: "",
    email: "",
    courses_amount: "",
    payment_method: "cb",
  });
  const [slots, setSlots] = useState([]);
  const [dayClosed, setDayClosed] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const estimateTimer = useRef(null);

  useEffect(() => {
    if (!form.date) {
      setSlots([]);
      setDayClosed(null);
      return;
    }
    api
      .get(`/slots?date=${form.date}`)
      .then(({ data }) => {
        setSlots(data.slots);
        setDayClosed(data.closed ? data.closed_reason || "ferme" : null);
        setForm((f) => {
          const current = data.slots.find((s) => s.value === f.time_slot && s.available);
          if (current) return f;
          const first = data.slots.find((s) => s.available);
          return { ...f, time_slot: first ? first.value : "" };
        });
      })
      .catch(() => setSlots([]));
  }, [form.date]);

  useEffect(() => {
    clearTimeout(estimateTimer.current);
    if (form.pickup_address.trim().length < 5 || form.delivery_address.trim().length < 5) {
      setEstimate(null);
      setEstimating(false);
      return;
    }
    setEstimating(true);
    estimateTimer.current = setTimeout(() => {
      api
        .get("/estimate", { params: { pickup: form.pickup_address, delivery: form.delivery_address } })
        .then(({ data }) => setEstimate(data))
        .catch(() => setEstimate({ distance_km: null, price: 8, estimated: false }))
        .finally(() => setEstimating(false));
    }, 700);
    return () => clearTimeout(estimateTimer.current);
  }, [form.pickup_address, form.delivery_address]);

  const courses = parseFloat(form.courses_amount) || 0;
  const deliveryPrice = estimate?.price ?? null;
  const total = (deliveryPrice ?? 0) + courses;
  const noSlotAvailable = form.date && slots.length > 0 && slots.every((s) => !s.available);
  const isCb = form.payment_method === "cb";
  const typeLabel = useMemo(() => TYPES.find((t) => t.key === form.delivery_type)?.label, [form.delivery_type]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        courses_amount: courses,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        navigate(`/reservation/confirmee?ref=${data.booking_ref}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez ou appelez-moi au 06 73 31 82 36.");
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="reserver" data-testid="booking-form-section" className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 max-w-2xl"
        >
          <span className="mb-4 inline-flex w-fit rounded-full bg-terra/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-terra-dark">
            Réservation en ligne
          </span>
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Prenez rendez-vous en <em className="italic text-forest">deux minutes</em>
          </h2>
          <p className="mt-3 text-base text-ink/65 sm:text-lg">
            Pas de compte à créer. Le prix se calcule tout seul selon la distance réelle entre vos deux adresses —
            entre 5 et 15 €, jamais plus.
          </p>
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-12">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-ink/5 sm:p-10 lg:col-span-8"
          >
            <fieldset className="mb-8">
              <legend className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-ink/50">
                Type de livraison
              </legend>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="radiogroup" aria-label="Type de livraison">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="radio"
                    aria-checked={form.delivery_type === t.key}
                    onClick={() => setForm((f) => ({ ...f, delivery_type: t.key }))}
                    data-testid={`select-delivery-type-${t.key}`}
                    className={`flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-2xl border p-3 text-center text-sm font-semibold transition-all duration-200 ${
                      form.delivery_type === t.key
                        ? "border-forest bg-forest/5 text-forest shadow-[0_0_0_1px_#0E7FA6]"
                        : "border-ink/10 text-ink/60 hover:border-forest/40 hover:text-forest"
                    }`}
                  >
                    <t.icon className="h-6 w-6" />
                    {t.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Adresse de départ (point A)</span>
                <input
                  required
                  value={form.pickup_address}
                  onChange={set("pickup_address")}
                  placeholder="Ex. Drive Intermarché, rue de l'Industrie, Illzach"
                  className={inputCls}
                  data-testid="input-pickup-address"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Adresse de livraison (point B)</span>
                <input
                  required
                  value={form.delivery_address}
                  onChange={set("delivery_address")}
                  placeholder="Ex. 4 avenue de la République, Mulhouse"
                  className={inputCls}
                  data-testid="input-delivery-address"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Date souhaitée</span>
                <input
                  required
                  type="date"
                  min={today}
                  value={form.date}
                  onChange={set("date")}
                  className={inputCls}
                  data-testid="input-delivery-date"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Créneau</span>
                <select
                  required
                  value={form.time_slot}
                  onChange={set("time_slot")}
                  className={inputCls}
                  data-testid="input-delivery-time"
                  disabled={!form.date}
                >
                  {!form.date && <option value="">Choisissez d'abord une date</option>}
                  {form.date && !dayClosed && slots.length === 0 && <option value="">Chargement des créneaux…</option>}
                  {dayClosed && <option value="">Fermé ce jour</option>}
                  {slots.map((s) => (
                    <option key={s.value} value={s.value} disabled={!s.available}>
                      {s.value}
                      {s.available ? "" : " — complet"}
                    </option>
                  ))}
                </select>
              </label>
              {dayClosed && (
                <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 sm:col-span-2" data-testid="closed-day-message">
                  <CalendarX2 className="h-4 w-4 shrink-0" />
                  {dayClosed === "ferie"
                    ? "Jour férié : je ne livre pas ce jour-là. Choisissez une autre date, ou appelez le 06 73 31 82 36."
                    : "Je ne livre pas le dimanche. Choisissez un jour du lundi au samedi, ou appelez le 06 73 31 82 36."}
                </p>
              )}
              {noSlotAvailable && (
                <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 sm:col-span-2" data-testid="no-slot-message">
                  <CalendarX2 className="h-4 w-4 shrink-0" />
                  Cette journée est complète. Choisissez une autre date ou appelez le 06 73 31 82 36.
                </p>
              )}
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-sm font-semibold text-ink">
                  Ce que je dois récupérer <span className="font-normal text-ink/40">(facultatif)</span>
                </span>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="Ex. Commande drive n°4521 au nom de Dupont, 3 sacs + 1 pack d'eau"
                  className={`${inputCls} py-3`}
                  data-testid="input-description"
                />
              </label>
            </div>

            <fieldset className="mt-8">
              <legend className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-ink/50">
                Mode de paiement
              </legend>
              <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Mode de paiement">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    role="radio"
                    aria-checked={form.payment_method === m.key}
                    onClick={() => setForm((f) => ({ ...f, payment_method: m.key }))}
                    data-testid={`select-payment-${m.key}`}
                    className={`flex min-h-[72px] items-center gap-3 rounded-2xl border p-4 text-left transition-all duration-200 ${
                      form.payment_method === m.key
                        ? "border-forest bg-forest/5 shadow-[0_0_0_1px_#0E7FA6]"
                        : "border-ink/10 hover:border-forest/40"
                    }`}
                  >
                    <m.icon className={`h-6 w-6 shrink-0 ${form.payment_method === m.key ? "text-forest" : "text-ink/40"}`} />                    <span>
                      <span className={`block text-sm font-semibold ${form.payment_method === m.key ? "text-forest" : "text-ink"}`}>
                        {m.label}
                      </span>
                      <span className="block text-xs text-ink/50">{m.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="my-8 border-t border-ink/10" />

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Votre nom</span>
                <input
                  required
                  value={form.client_name}
                  onChange={set("client_name")}
                  placeholder="Prénom et nom"
                  className={inputCls}
                  data-testid="input-client-name"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Votre téléphone</span>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="06 00 00 00 00"
                  className={inputCls}
                  data-testid="input-client-phone"
                />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-sm font-semibold text-ink">Votre e-mail</span>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="vous@exemple.fr"
                  className={inputCls}
                  data-testid="input-client-email"
                />
              </label>
            </div>

            {error && (
              <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="booking-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !form.time_slot || !estimate}
              data-testid="button-proceed-payment"
              className="mt-8 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-terra px-8 text-lg font-semibold text-white shadow-[0_10px_24px_-10px_#EE6FA3] transition-all duration-200 hover:-translate-y-0.5 hover:bg-terra-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> {isCb ? "Redirection vers le paiement…" : "Enregistrement…"}
                </>
              ) : !estimate ? (
                <>Indiquez vos deux adresses pour voir le prix</>
              ) : isCb ? (
                <>
                  <Lock className="h-5 w-5" /> Réserver et payer {eur(total)} € par carte
                </>
              ) : (
                <>Réserver — {eur(total)} € {form.payment_method === "especes" ? "en espèces à la livraison" : "par virement"}</>
              )}
            </button>
            <p className="mt-3 text-center text-sm text-ink/50">
              {isCb
                ? "Paiement sécurisé par carte bancaire. Le tarif est confirmé avec vous par téléphone après la réservation."
                : "Aucun paiement en ligne : je vous appelle pour confirmer, et vous réglez comme convenu. Un e-mail récapitulatif vous est envoyé."}
            </p>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4"
          >
            <div className="sticky top-28 rounded-3xl bg-forest p-6 text-cream sm:p-8" data-testid="price-estimator">
              <h3 className="font-serif text-xl font-medium sm:text-2xl">Votre estimation</h3>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="border-b border-cream/10 pb-3">
                  <div className="flex items-center justify-between">
                    <dt className="text-cream/70">Livraison {typeLabel}</dt>
                    <dd className="text-base font-semibold" data-testid="estimated-delivery-price">
                      {estimating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : deliveryPrice !== null ? (
                        `${eur(deliveryPrice)} €`
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                  {estimate?.distance_km !== null && estimate?.distance_km !== undefined && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-cream/55" data-testid="estimated-distance">
                      <MapPinned className="h-3.5 w-3.5 text-terra" />
                      ≈ {String(estimate.distance_km).replace(".", ",")} km en voiture entre A et B
                    </p>
                  )}
                  {estimate && !estimate.estimated && (
                    <p className="mt-1.5 text-xs text-cream/55">
                      Distance non trouvée : tarif provisoire de 8 €, ajusté ensemble au téléphone.
                    </p>
                  )}
                </div>
                <div className="pb-1">
                  <label className="block">
                    <span className="mb-1.5 block text-cream/70">
                      Montant de vos courses à avancer <span className="text-cream/40">(facultatif)</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="500"
                        step="0.01"
                        value={form.courses_amount}
                        onChange={set("courses_amount")}
                        placeholder="0,00"
                        data-testid="input-courses-amount"
                        className="min-h-[48px] w-full rounded-xl border border-cream/20 bg-forest-dark/50 px-4 text-base text-cream outline-none placeholder:text-cream/30 focus:border-terra"
                      />
                      <span className="text-base font-semibold">€</span>
                    </span>
                  </label>
                  <p className="mt-2 text-xs leading-relaxed text-cream/50">
                    Le montant du ticket de caisse, si vous souhaitez tout régler en une fois. Sinon, on s'arrange à la livraison.
                  </p>
                </div>
                <div className="flex items-center justify-between border-t border-cream/15 pt-4">
                  <dt className="font-serif text-lg">Total estimé</dt>
                  <dd className="font-serif text-2xl font-semibold text-terra" data-testid="estimated-total">
                    {deliveryPrice !== null ? `${eur(total)} €` : "—"}
                  </dd>
                </div>
              </dl>
              <p className="mt-5 rounded-xl bg-cream/10 px-4 py-3 text-xs leading-relaxed text-cream/70">
                5 € jusqu'à 5 km, puis 0,50 € par kilomètre supplémentaire — plafonné à 15 €, jamais plus. Tarif
                confirmé par téléphone, aucun supplément sans votre accord.
              </p>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
